// Pure helpers behind the BucketGrid (design-system §3.14): weekly dates, the
// quantity box and the spreadsheet paste. No React here, so each rule is
// unit-tested on its own.

const DAY_MS = 86_400_000;
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** UTC midnight of a real `YYYY-MM-DD` date, or undefined. */
function isoToMs(iso: string): number | undefined {
  const m = ISO_RE.exec(iso);
  if (m === null) return undefined;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const at = Date.UTC(y, mo - 1, d);
  const back = new Date(at);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
    return undefined;
  }
  return at;
}

function msToIso(ms: number): string {
  const at = new Date(ms);
  const y = String(at.getUTCFullYear()).padStart(4, "0");
  const mo = String(at.getUTCMonth() + 1).padStart(2, "0");
  const d = String(at.getUTCDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
}

/** True for a real calendar date written `YYYY-MM-DD`. */
export function isIsoDate(value: string): boolean {
  return isoToMs(value) !== undefined;
}

/** The date `days` after `iso` (negative goes back). */
export function addDaysIso(iso: string, days: number): string {
  const ms = isoToMs(iso);
  if (ms === undefined) throw new RangeError(`not a date: ${iso}`);
  return msToIso(ms + days * DAY_MS);
}

/** The Monday of the ISO week holding `iso`. */
export function mondayOfIso(iso: string): string {
  const ms = isoToMs(iso);
  if (ms === undefined) throw new RangeError(`not a date: ${iso}`);
  const weekday = (new Date(ms).getUTCDay() + 6) % 7;
  return msToIso(ms - weekday * DAY_MS);
}

/** Whole weeks from one Monday to another. */
export function weeksBetween(fromMonday: string, toMonday: string): number {
  const a = isoToMs(fromMonday);
  const b = isoToMs(toMonday);
  if (a === undefined || b === undefined) return 0;
  return Math.round((b - a) / (7 * DAY_MS));
}

/** The Mondays from `from` to `to`, both inclusive. */
export function mondaysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  const count = weeksBetween(from, to);
  for (let i = 0; i <= count; i += 1) out.push(addDaysIso(from, i * 7));
  return out;
}

/** The ISO week number of a date, for headers such as "W42". */
export function isoWeekNumber(iso: string): number {
  const monday = isoToMs(mondayOfIso(iso)) ?? 0;
  const thursday = new Date(monday + 3 * DAY_MS);
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  return Math.floor((thursday.getTime() - yearStart) / (7 * DAY_MS)) + 1;
}

const QTY_PLACES = 3;

export type ParsedQty =
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly reason: "not_number" | "precision" };

interface QtyMarks {
  readonly decimal: string;
  readonly group: string;
}

const qtyMarks = new Map<string, QtyMarks>();

// The decimal and grouping marks the reader's locale writes numbers with.
function localeQtyMarks(locale: string): QtyMarks {
  let marks = qtyMarks.get(locale);
  if (marks === undefined) {
    marks = { decimal: ".", group: "," };
    try {
      const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
      const decimal = parts.find((p) => p.type === "decimal")?.value;
      const group = parts.find((p) => p.type === "group")?.value;
      if (decimal !== undefined) marks = { decimal, group: group ?? "" };
    } catch {
      // An unknown tag keeps the default marks.
    }
    qtyMarks.set(locale, marks);
  }
  return marks;
}

const GROUPED_RE = /^[1-9]\d{0,2}$/u;

// Thousands groups after a leading group of one to three digits.
function validGroups(groups: readonly string[]): boolean {
  const [head, ...rest] = groups;
  return head !== undefined && GROUPED_RE.test(head) && rest.every((g) => /^\d{3}$/u.test(g));
}

/**
 * Reads a typed or pasted quantity as a plain decimal string. Spaces group
 * thousands. A lone mark with three digits after it is read the way the
 * reader's locale writes numbers; every other shape has one reading.
 */
export function parseGridQty(text: string, locale: string): ParsedQty {
  const raw = text.replace(/[\s\u00a0\u202f']/gu, "");
  if (raw.length === 0 || !/^[0-9.,]+$/u.test(raw)) return { ok: false, reason: "not_number" };
  const dots = raw.split(".").length - 1;
  const commas = raw.split(",").length - 1;
  let intPart = raw;
  let fracPart = "";
  if (dots > 0 && commas > 0) {
    // Both appear: the later one is the decimal mark, the other groups.
    const decimal = raw.lastIndexOf(".") > raw.lastIndexOf(",") ? "." : ",";
    const group = decimal === "." ? "," : ".";
    if (raw.split(decimal).length - 1 > 1) return { ok: false, reason: "not_number" };
    const at = raw.lastIndexOf(decimal);
    const groups = raw.slice(0, at).split(group);
    if (!validGroups(groups)) return { ok: false, reason: "not_number" };
    intPart = groups.join("");
    fracPart = raw.slice(at + 1);
  } else if (dots + commas > 1) {
    // One mark repeated can only group thousands: 1.000.000 or 1,000,000.
    const groups = raw.split(dots > 0 ? "." : ",");
    if (!validGroups(groups)) return { ok: false, reason: "not_number" };
    intPart = groups.join("");
  } else if (dots + commas === 1) {
    const mark = dots > 0 ? "." : ",";
    const at = raw.indexOf(mark);
    const groups = [raw.slice(0, at), raw.slice(at + 1)];
    if (validGroups(groups) && localeQtyMarks(locale).group === mark) {
      intPart = groups.join("");
    } else {
      intPart = raw.slice(0, at);
      fracPart = raw.slice(at + 1);
    }
  }
  if (!/^\d+$/u.test(intPart === "" ? "0" : intPart) || !/^\d*$/u.test(fracPart)) {
    return { ok: false, reason: "not_number" };
  }
  const trimmedFrac = fracPart.replace(/0+$/u, "");
  if (trimmedFrac.length > QTY_PLACES) return { ok: false, reason: "precision" };
  const int = (intPart === "" ? "0" : intPart).replace(/^0+(?=\d)/u, "");
  return { ok: true, value: trimmedFrac.length > 0 ? `${int}.${trimmedFrac}` : int };
}

const numberFormats = new Map<string, Intl.NumberFormat>();

/**
 * A stored decimal in the reader's locale, at most three places. `signed`
 * writes a plus before a positive value, for a change rather than a quantity.
 */
export function formatGridQty(
  value: string | null | undefined,
  locale: string,
  signed = false,
): string {
  if (value === null || value === undefined || value === "") return "";
  const cacheKey = signed ? `${locale}|signed` : locale;
  let fmt = numberFormats.get(cacheKey);
  if (fmt === undefined) {
    fmt = new Intl.NumberFormat(locale, {
      maximumFractionDigits: QTY_PLACES,
      ...(signed ? { signDisplay: "exceptZero" as const } : {}),
    });
    numberFormats.set(cacheKey, fmt);
  }
  const n = Number(value);
  return Number.isFinite(n) ? fmt.format(n) : value;
}

/**
 * A stored decimal as an editor's starting text: no grouping and the locale's
 * decimal mark, built from the string so opening and closing a field changes nothing.
 */
export function editGridQty(value: string | null | undefined, locale: string): string {
  if (value === null || value === undefined || value === "") return "";
  const m = /^(\d+)(?:\.(\d*))?$/u.exec(value);
  if (m === null) return value;
  const int = (m[1] ?? "0").replace(/^0+(?=\d)/u, "");
  const frac = (m[2] ?? "").replace(/0+$/u, "");
  return frac.length > 0 ? `${int}${localeQtyMarks(locale).decimal}${frac}` : int;
}

/** Two decimals are the same quantity (`3000` and `3000.000`). */
export function sameQty(a: string | null | undefined, b: string | null | undefined): boolean {
  const empty = (v: string | null | undefined) => v === null || v === undefined || v === "";
  if (empty(a) || empty(b)) return empty(a) && empty(b);
  return Number(a) === Number(b);
}

const dateOrders = new Map<string, readonly ("day" | "month" | "year")[]>();

// The order the reader's locale writes day, month and year in.
function localeDateOrder(locale: string): readonly ("day" | "month" | "year")[] {
  let order = dateOrders.get(locale);
  if (order === undefined) {
    try {
      const parts = new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone: "UTC",
      }).formatToParts(Date.UTC(2026, 2, 25));
      const found = parts
        .map((p) => p.type)
        .filter((t): t is "day" | "month" | "year" => t === "day" || t === "month" || t === "year");
      order = found.length === 3 ? found : ["day", "month", "year"];
    } catch {
      order = ["day", "month", "year"];
    }
    dateOrders.set(locale, order);
  }
  return order;
}

/** An ISO date, or day, month and year in the reader's locale, as `YYYY-MM-DD`. */
export function parseGridDate(text: string, locale: string): string | undefined {
  const raw = text.trim();
  if (ISO_RE.test(raw)) return isIsoDate(raw) ? raw : undefined;
  const m = /^(\d{1,4})[./-](\d{1,2})[./-](\d{1,4})$/u.exec(raw);
  if (m === null) return undefined;
  const order = localeDateOrder(locale);
  const values: Record<string, number> = {};
  [m[1], m[2], m[3]].forEach((v, i) => {
    const key = order[i];
    if (key !== undefined) values[key] = Number(v);
  });
  let year = values["year"] ?? 0;
  if (year < 100) year += 2000;
  const iso = `${String(year).padStart(4, "0")}-${String(values["month"] ?? 0).padStart(2, "0")}-${String(values["day"] ?? 0).padStart(2, "0")}`;
  return isIsoDate(iso) ? iso : undefined;
}

/** A spreadsheet block split into its header, its product column and its values. */
export interface PasteBlock {
  /** One parsed date per value column when the first line is a week header. */
  readonly header: readonly (string | undefined)[] | undefined;
  /** The header cells as written, for hints. */
  readonly headerText: readonly string[];
  /** The first column's cells when they name products. */
  readonly productKeys: readonly string[] | undefined;
  /** The value cells, one array per data line. */
  readonly values: readonly (readonly string[])[];
  /** The locale the values are read in. */
  readonly locale: string;
}

/**
 * Splits a tab-separated block. The first line is a week header when every cell
 * after its first parses as a date; the first column names products when any of
 * its data cells is not a number.
 */
export function parsePasteText(text: string, locale: string): PasteBlock {
  const lines = text
    .replace(/\r\n?/gu, "\n")
    .split("\n")
    .filter((line, i, all) => !(i === all.length - 1 && line.trim() === ""))
    .map((line) => line.split("\t"));
  if (lines.length === 0) {
    return { header: undefined, headerText: [], productKeys: undefined, values: [], locale };
  }
  const first = lines[0] ?? [];
  const tail = first.slice(1);
  const isHeader = tail.length > 0 && tail.every((c) => parseGridDate(c, locale) !== undefined);
  const dataLines = isHeader ? lines.slice(1) : lines;
  const firstCells = dataLines.map((l) => (l[0] ?? "").trim());
  const hasProducts = firstCells.some((c) => c.length > 0 && !parseGridQty(c, locale).ok);
  // Without a product column a header's first cell is a week too.
  const headerCells = isHeader ? (hasProducts ? tail : first) : [];
  return {
    header: isHeader ? headerCells.map((c) => parseGridDate(c, locale)) : undefined,
    headerText: headerCells.map((c) => c.trim()),
    productKeys: hasProducts ? firstCells : undefined,
    values: dataLines.map((l) => (hasProducts ? l.slice(1) : l)),
    locale,
  };
}

export type PasteProblemKind =
  | "not_number"
  | "precision"
  | "week_outside"
  | "unknown_product"
  | "lookup_failed"
  | "over_key_limit";

export interface PasteProblem {
  /** 1-based line of the pasted block. */
  readonly line: number;
  /** 1-based column of the pasted block. */
  readonly column: number;
  readonly kind: PasteProblemKind;
  readonly text: string;
}

export interface PasteHint {
  readonly column: number;
  readonly text: string;
  readonly date: string;
  readonly monday: string;
}

export interface PasteWrite {
  readonly rowKey: string;
  readonly week: string;
  readonly value: string;
}

export interface PastePlan {
  readonly writes: readonly PasteWrite[];
  /** Row keys the block names that the grid does not show yet. */
  readonly newRowKeys: readonly string[];
  readonly problems: readonly PasteProblem[];
  readonly hints: readonly PasteHint[];
}

export interface PastePlanContext {
  /** The weeks a paste may write, in column order. */
  readonly weeks: readonly string[];
  /** Weeks shown that may not be written (past weeks). */
  readonly readOnlyWeeks?: ReadonlySet<string>;
  /** The grid's row keys in order. */
  readonly rowKeys: readonly string[];
  /** The focused cell, where a block without header or product column starts. */
  readonly anchorRow: number;
  readonly anchorWeek: number;
  /** A product key's row key, or undefined when the caller does not know it. */
  readonly resolveRow?: (key: string) => string | undefined;
  /** Why a key has no row when it is not simply unknown, such as a failed lookup. */
  readonly unresolvedKind?: (key: string) => PasteProblemKind | undefined;
}

/** Maps a parsed block onto the grid and says what it could not take. */
export function planPaste(block: PasteBlock, ctx: PastePlanContext): PastePlan {
  const writes: PasteWrite[] = [];
  const problems: PasteProblem[] = [];
  const hints: PasteHint[] = [];
  const newRowKeys: string[] = [];
  const lineOffset = block.header !== undefined ? 2 : 1;
  const colOffset = block.productKeys !== undefined ? 2 : 1;
  const writable = new Set(ctx.weeks.filter((w) => !(ctx.readOnlyWeeks?.has(w) ?? false)));

  const weekOf: (string | undefined)[] = [];
  const width = Math.max(0, ...block.values.map((v) => v.length));
  for (let c = 0; c < width; c += 1) {
    if (block.header !== undefined) {
      const date = block.header[c];
      if (date === undefined) {
        weekOf.push(undefined);
        continue;
      }
      const monday = mondayOfIso(date);
      if (monday !== date) {
        hints.push({ column: c + colOffset, text: block.headerText[c] ?? date, date, monday });
      }
      weekOf.push(monday);
    } else {
      weekOf.push(ctx.weeks[ctx.anchorWeek + c]);
    }
  }

  block.values.forEach((cells, r) => {
    const line = r + lineOffset;
    let rowKey: string | undefined;
    if (block.productKeys !== undefined) {
      const key = block.productKeys[r] ?? "";
      rowKey = key.length > 0 ? ctx.resolveRow?.(key) : undefined;
      if (rowKey === undefined) {
        const kind = (key.length > 0 ? ctx.unresolvedKind?.(key) : undefined) ?? "unknown_product";
        problems.push({ line, column: 1, kind, text: key });
        return;
      }
      if (!ctx.rowKeys.includes(rowKey) && !newRowKeys.includes(rowKey)) newRowKeys.push(rowKey);
    } else {
      rowKey = ctx.rowKeys[ctx.anchorRow + r];
      if (rowKey === undefined) {
        // The block runs past the last row the grid shows.
        problems.push({ line, column: 1, kind: "unknown_product", text: "" });
        return;
      }
    }
    cells.forEach((cell, c) => {
      const column = c + colOffset;
      if (cell.trim() === "") return;
      const week = weekOf[c];
      if (week === undefined || !writable.has(week)) {
        problems.push({ line, column, kind: "week_outside", text: cell });
        return;
      }
      const qty = parseGridQty(cell, block.locale);
      if (!qty.ok) {
        problems.push({ line, column, kind: qty.reason, text: cell });
        return;
      }
      writes.push({ rowKey, week, value: qty.value });
    });
  });
  return { writes, newRowKeys, problems, hints };
}
